/**
 * ai.js — Provider-agnostic LLM Client for SPEKTRX
 *
 * OpenAI-compatible API client that works with:
 * - OpenAI (api.openai.com)
 * - Azure OpenAI
 * - Ollama (localhost:11434)
 * - LM Studio (localhost:1234)
 * - Any OpenAI-compatible endpoint
 *
 * Zero Supabase risk — all AI calls go to user-configured endpoint.
 * API key stored in S.settings (persisted in localStorage/IDB, synced via existing cloud).
 */

import { S } from './state.js';
import { breakers } from './resilience.js';

// ─── DEFAULTS ──────────────────────────────────────────────────────

const DEFAULT_PROVIDERS = {
  anthropic: { url: 'https://api.anthropic.com', model: 'claude-sonnet-4-6' },
  openai: { url: 'https://api.openai.com/v1', model: 'gpt-4o-mini' },
  ollama: { url: 'http://localhost:11434/v1', model: 'llama3.1' },
  lmstudio: { url: 'http://localhost:1234/v1', model: 'local-model' },
};

// ─── CONFIG HELPERS ────────────────────────────────────────────────

// Deprecated model IDs → current replacements
const MODEL_MIGRATIONS = {
  'claude-sonnet-4-20250514': 'claude-sonnet-4-6',
};

/** Get AI configuration from settings */
export function getAIConfig() {
  const ai = S.settings.ai || {};
  const provider = ai.provider || 'anthropic';
  const defaults = DEFAULT_PROVIDERS[provider] || DEFAULT_PROVIDERS.anthropic;
  let model = ai.model || defaults.model;
  // Auto-migrate deprecated model IDs
  if (MODEL_MIGRATIONS[model]) model = MODEL_MIGRATIONS[model];
  return {
    provider,
    url: (ai.url || defaults.url).replace(/\/+$/, ''),
    apiKey: ai.apiKey || '',
    model,
    maxTokens: ai.maxTokens || 2000,
    temperature: ai.temperature ?? 0.1,
  };
}

/** Check if AI is configured (has API key or is local provider) */
export function isAIConfigured() {
  const cfg = getAIConfig();
  const isLocal = cfg.url.includes('localhost') || cfg.url.includes('127.0.0.1');
  return isLocal || !!cfg.apiKey;
}

/** Get default config for a known provider */
export function getProviderDefaults(provider) {
  return DEFAULT_PROVIDERS[provider] || DEFAULT_PROVIDERS.openai;
}

// ─── CORE API CALL ─────────────────────────────────────────────────

/**
 * Send a chat completion request to the configured LLM endpoint.
 *
 * @param {Array<{role: string, content: string}>} messages - Chat messages
 * @param {Object} opts - Override options: { model, maxTokens, temperature, json }
 * @returns {Promise<{content: string, usage: Object, model: string}>}
 * @throws {AIError} on network, auth, or API errors
 */
export async function chatCompletion(messages, opts = {}) {
  const cfg = getAIConfig();

  if (!isAIConfigured()) {
    throw new AIError('AI not configured. Go to Settings → AI to set up your provider.', 'CONFIG');
  }

  const isAnthropic = cfg.provider === 'anthropic';

  // ─── Build request based on provider ───
  let url, headers, body;

  if (isAnthropic) {
    url = cfg.url + '/v1/messages';
    headers = {
      'Content-Type': 'application/json',
      'x-api-key': cfg.apiKey,
      'anthropic-version': '2023-06-01',
      'anthropic-dangerous-direct-browser-access': 'true',
    };

    // Anthropic: system is a top-level param, not in messages array
    const systemMsg = messages.find(m => m.role === 'system');
    const nonSystemMsgs = messages.filter(m => m.role !== 'system');

    body = {
      model: opts.model || cfg.model,
      max_tokens: opts.maxTokens || cfg.maxTokens,
      temperature: opts.temperature ?? cfg.temperature,
      messages: nonSystemMsgs,
    };
    if (systemMsg) body.system = systemMsg.content;

  } else {
    // OpenAI-compatible (OpenAI, Ollama, LM Studio, etc.)
    url = cfg.url + '/chat/completions';
    headers = { 'Content-Type': 'application/json' };
    if (cfg.apiKey) headers['Authorization'] = 'Bearer ' + cfg.apiKey;

    body = {
      model: opts.model || cfg.model,
      messages,
      max_tokens: opts.maxTokens || cfg.maxTokens,
      temperature: opts.temperature ?? cfg.temperature,
    };

    // Request JSON mode if supported
    if (opts.json) {
      body.response_format = { type: 'json_object' };
    }
  }

  // AI-1: circuit breaker — back off after repeated provider/network failures (don't hammer the API or the user)
  if (!breakers.ai.canRequest()) {
    throw new AIError('AI temporarily unavailable — too many recent failures, backing off (~1 min). Try again shortly.', 'CIRCUIT');
  }

  let res;
  try {
    res = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      // A caller-supplied signal used to SHORT-CIRCUIT the timeout entirely —
      // the main scan call could hang forever on a stuck provider (audit W1-4).
      // Now: caller signal AND timeout race; whichever aborts first wins.
      signal: opts.signal
        ? (typeof AbortSignal.any === 'function'
          ? AbortSignal.any([opts.signal, AbortSignal.timeout(opts.timeoutMs || 30000)])
          : opts.signal)
        : AbortSignal.timeout(opts.timeoutMs || 30000),
    });
  } catch (e) {
    breakers.ai.recordFailure(); // network/timeout — counts toward opening the circuit
    if (e.name === 'AbortError' || e.name === 'TimeoutError') {
      throw new AIError('AI request timed out. Check your provider is running.', 'TIMEOUT');
    }
    throw new AIError('Cannot reach AI provider at ' + cfg.url + '. ' + e.message, 'NETWORK');
  }

  if (!res.ok) {
    const status = res.status;
    // AI-1: only provider-side failures (5xx) and rate limits trip the breaker — not user config errors (401/403/404)
    if (status >= 500 || status === 429) breakers.ai.recordFailure();
    let errMsg = `AI API error (${status})`;
    try {
      const errBody = await res.json();
      errMsg = errBody.error?.message || errBody.message || errMsg;
    } catch (_e) { /* ignore parse failure */ }

    if (status === 401 || status === 403) {
      throw new AIError('Invalid API key. Check Settings → AI.', 'AUTH');
    }
    if (status === 429) {
      throw new AIError('Rate limited. Wait a moment and try again.', 'RATE_LIMIT');
    }
    if (status === 404) {
      throw new AIError('Model not found: ' + (opts.model || cfg.model) + '. Check your model name in Settings → AI.', 'MODEL');
    }
    throw new AIError(errMsg, 'API', status);
  }

  breakers.ai.recordSuccess(); // AI-1: healthy response resets the breaker
  const data = await res.json();

  // ─── Parse response based on provider ───
  if (isAnthropic) {
    const textBlock = (data.content || []).find(b => b.type === 'text');
    if (!textBlock?.text) {
      throw new AIError('Empty response from AI provider.', 'EMPTY');
    }
    return {
      content: textBlock.text.trim(),
      usage: data.usage || {},
      model: data.model || cfg.model,
      finishReason: data.stop_reason,
    };
  } else {
    const choice = data.choices?.[0];
    if (!choice?.message?.content) {
      throw new AIError('Empty response from AI provider.', 'EMPTY');
    }
    return {
      content: choice.message.content.trim(),
      usage: data.usage || {},
      model: data.model || cfg.model,
      finishReason: choice.finish_reason,
    };
  }
}

// ─── JSON EXTRACTION ───────────────────────────────────────────────

/**
 * Send a chat completion and parse the response as JSON.
 * Handles markdown code fences, partial JSON, etc.
 *
 * @param {Array} messages - Chat messages
 * @param {Object} opts - Same as chatCompletion
 * @returns {Promise<Object>} Parsed JSON object
 */
export async function chatJSON(messages, opts = {}) {
  const result = await chatCompletion(messages, { ...opts, json: true });
  let content = result.content;

  // TRUNCATION INTEGRITY (audit 2026-08-01): a response cut off at the token cap is
  // not malformed JSON, it is a budget failure. Reporting it as 'PARSE' is what hid a
  // dead news-impact stage for 13 days — the fallback regex below anchors to a closing
  // brace, which a mid-string truncation can never match, so it always threw 'PARSE'.
  // Anthropic reports 'max_tokens'; OpenAI-compatible providers report 'length'.
  const fr = result.finishReason;
  if (fr === 'length' || fr === 'max_tokens') {
    throw new AIError(
      'AI response truncated at the token cap (max_tokens=' + (opts.maxTokens || 'provider default')
      + '). Raise the cap for this call or request less output.', 'TRUNCATED');
  }

  // Strip markdown code fences if present
  content = content.replace(/^```(?:json)?\s*\n?/i, '').replace(/\n?```\s*$/i, '');

  try {
    const parsed = JSON.parse(content);
    return { data: parsed, usage: result.usage, model: result.model };
  } catch (_e) {
    // Try to find JSON object/array in the response (anchor to end to avoid greedy over-capture)
    const jsonMatch = content.match(/[\[{][\s\S]*[\]}]\s*$/);
    if (jsonMatch) {
      try {
        const parsed = JSON.parse(jsonMatch[0]);
        return { data: parsed, usage: result.usage, model: result.model };
      } catch (_e2) { /* fall through */ }
    }
    throw new AIError('Failed to parse AI response as JSON: ' + content.slice(0, 200), 'PARSE');
  }
}

// ─── CONNECTION TEST ───────────────────────────────────────────────

/**
 * Test AI connection with a simple prompt.
 * @returns {Promise<{ok: boolean, model: string, latencyMs: number, error?: string}>}
 */
export async function testAIConnection() {
  const start = Date.now();
  try {
    const result = await chatCompletion(
      [{ role: 'user', content: 'Reply with exactly: OK' }],
      { maxTokens: 10, temperature: 0 }
    );
    return {
      ok: true,
      model: result.model,
      latencyMs: Date.now() - start,
      response: result.content,
    };
  } catch (e) {
    return {
      ok: false,
      error: e.message || String(e),
      code: e.code || 'UNKNOWN',
      latencyMs: Date.now() - start,
    };
  }
}

// ─── ERROR CLASS ───────────────────────────────────────────────────

export class AIError extends Error {
  constructor(message, code, status) {
    super(message);
    this.name = 'AIError';
    this.code = code;
    this.status = status;
  }
}
